import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DashboardShell } from "@/components/DashboardShell";
import { DashboardSkeleton } from "@/components/PageSkeleton";
import { planLimit } from "@/lib/plan-limits";
import { motion } from "framer-motion";
import {
  MessageSquare,
  Users,
  Network,
  ArrowUpRight,
  ArrowLeftRight,
  Activity,
  BrainCircuit,
  CheckCircle2,
  CreditCard,
  Flame,
  Link2,
  Store,
} from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";
import { getWorkspaceStatus } from "@/lib/workspace.functions";
import { getDashboardStats } from "@/lib/analytics.functions";
import { useAdminMode } from "@/hooks/use-admin-mode";

import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () =>
    buildSeo({
      path: "/dashboard",
      title: "Dashboard — Reality Twin",
      description:
        "Your Reality Twin control center — track conversations, leads, opportunities and Twin activity across your workspace at a glance.",
      noindex: true,
    }),
  component: Dashboard,
});

const PLAN_LABEL: Record<string, string> = {
  free: "Free",
  starter: "Starter",
  professional: "Pro",
  scale: "Scale",
};

function PlanUsage({ plan, used, limit }: { plan: string; used: number; limit: number }) {
  const { t } = useTranslation();
  const cap = limit > 0 ? limit : 1;
  const pct = Math.min(100, Math.round((used / cap) * 100));
  const nearLimit = pct >= 80;
  return (
    <div className="mt-5 pt-4 border-t border-border">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {t("app.dashboard.planWord", { defaultValue: "Plan" })}
        </span>
        <span className="font-medium">{PLAN_LABEL[plan] ?? plan}</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${pct}%`,
            background: nearLimit ? "oklch(0.7 0.19 40)" : "oklch(0.82 0.18 168)",
          }}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {used.toLocaleString()} / {limit.toLocaleString()}{" "}
          {t("app.dashboard.conversationsWord", { defaultValue: "conversations" })}
        </span>
        {nearLimit && plan !== "scale" && (
          <Link to="/billing" className="text-[oklch(0.82_0.18_168)] hover:underline">
            {t("app.dashboard.upgrade", { defaultValue: "Upgrade" })}
          </Link>
        )}
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground/70">
        {t("app.dashboard.sharedAcrossTwins", { defaultValue: "Shared across all your twins" })}
      </div>
    </div>
  );
}

type TFn = (key: string, opts?: any) => string;

function timeAgo(iso: string): string {
  const s = Math.max(1, Math.floor((Date.now() - Date.parse(iso)) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** Week-over-week change in percent; null when there is no baseline yet. */
function weekDelta(series: any[], key: string): number | null {
  const val = (r: any) => Number(r?.[key] || 0);
  const last7 = series.slice(-7).reduce((a, r) => a + val(r), 0);
  const prev7 = series.slice(-14, -7).reduce((a, r) => a + val(r), 0);
  if (prev7 === 0) return last7 > 0 ? null : 0;
  return Math.round(((last7 - prev7) / prev7) * 100);
}

function DeltaBadge({ delta }: { delta: number | null }) {
  if (delta === null)
    return <span className="text-[11px] font-mono text-[oklch(0.82_0.18_168)]">new</span>;
  if (delta === 0) return <span className="text-[11px] font-mono text-muted-foreground">±0%</span>;
  const up = delta > 0;
  return (
    <span
      className="text-[11px] font-mono"
      style={{ color: up ? "oklch(0.82 0.18 168)" : "oklch(0.72 0.19 25)" }}
    >
      {up ? "▲" : "▼"} {Math.abs(delta)}%
    </span>
  );
}

function Sparkline({ series, dataKey, hue }: { series: any[]; dataKey: string; hue: string }) {
  const points = series.map((r: any, i: number) => ({ i, v: Number(r[dataKey] || 0) }));
  return (
    <div className="absolute inset-x-0 bottom-0 h-12 opacity-60 pointer-events-none">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={`spark-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={`oklch(0.82 0.18 ${hue})`} stopOpacity={0.5} />
              <stop offset="100%" stopColor={`oklch(0.82 0.18 ${hue})`} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="v"
            stroke={`oklch(0.82 0.18 ${hue})`}
            strokeWidth={1.5}
            fill={`url(#spark-${dataKey})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function ScoreRing({ score }: { score: number }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  return (
    <div className="relative h-16 w-16">
      <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke="oklch(0.82 0.18 168)"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c - (c * pct) / 100}
          style={{ transition: "stroke-dashoffset 0.8s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-display font-semibold text-sm">
        {pct}
      </div>
    </div>
  );
}

type AttentionData = {
  hotLeads: number;
  newMatches: number;
  activeListings: number;
  openGaps?: number;
  isPublic: boolean;
  hasKnowledge: boolean;
} | null;

function buildActions(
  attention: AttentionData,
  usage: { plan: string; used: number; limit: number },
  t: TFn,
) {
  if (!attention) return [];
  const actions: Array<{
    icon: any;
    hue: string;
    text: string;
    count?: number;
    to: string;
    urgent?: boolean;
  }> = [];
  if (attention.hotLeads > 0)
    actions.push({
      icon: Flame,
      hue: "25",
      text: t(
        attention.hotLeads === 1 ? "app.dashboard.act.hot_one" : "app.dashboard.act.hot_other",
        {
          defaultValue:
            attention.hotLeads === 1
              ? "hot lead waiting for your follow-up"
              : "hot leads waiting for your follow-up",
        },
      ),
      count: attention.hotLeads,
      to: "/leads",
      urgent: true,
    });
  if (attention.newMatches > 0)
    actions.push({
      icon: ArrowLeftRight,
      hue: "168",
      text: t(
        attention.newMatches === 1
          ? "app.dashboard.act.match_one"
          : "app.dashboard.act.match_other",
        {
          defaultValue:
            attention.newMatches === 1
              ? "new Exchange match to review"
              : "new Exchange matches to review",
        },
      ),
      count: attention.newMatches,
      to: "/exchange",
    });
  if ((attention.openGaps ?? 0) > 0)
    actions.push({
      icon: BrainCircuit,
      hue: "290",
      text: t(
        (attention.openGaps ?? 0) === 1
          ? "app.dashboard.act.gap_one"
          : "app.dashboard.act.gap_other",
        {
          defaultValue:
            (attention.openGaps ?? 0) === 1
              ? "question your twin could not answer — close the gaps"
              : "questions your twin could not answer — close the gaps",
        },
      ),
      count: attention.openGaps,
      to: "/analytics",
    });
  if (!attention.hasKnowledge)
    actions.push({
      icon: BrainCircuit,
      hue: "290",
      text: t("app.dashboard.act.teach", {
        defaultValue: "Teach your twin — index your website or upload documents",
      }),
      to: "/knowledge",
    });
  if (!attention.isPublic)
    actions.push({
      icon: Store,
      hue: "240",
      text: t("app.dashboard.act.private", {
        defaultValue: "Your twin is private — publish it to appear in the marketplace",
      }),
      to: "/settings",
    });
  if (attention.activeListings === 0)
    actions.push({
      icon: ArrowLeftRight,
      hue: "60",
      text: t("app.dashboard.act.firstDeal", {
        defaultValue: "Post your first deal on the Exchange — matching runs automatically",
      }),
      to: "/exchange",
    });
  const cap = usage.limit > 0 ? usage.limit : 1;
  if (usage.used / cap >= 0.8 && usage.plan !== "scale")
    actions.push({
      icon: CreditCard,
      hue: "25",
      text: t("app.dashboard.act.usage", {
        used: usage.used.toLocaleString(),
        limit: usage.limit.toLocaleString(),
        defaultValue: "{{used}} of {{limit}} conversations used — upgrade before your twin pauses",
      }),
      to: "/billing",
      urgent: true,
    });
  return actions.slice(0, 4);
}

function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fetchWorkspace = useServerFn(getWorkspaceStatus);
  const fetchStats = useServerFn(getDashboardStats);
  const { viewAsUser } = useAdminMode();
  const [workspace, setWorkspace] = useState<any>(null);
  const [statsData, setStatsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    fetchWorkspace()
      .then(async (data) => {
        if (cancelled) return;
        if (data.betaStatus === "admin" && !viewAsUser) {
          navigate({ to: "/admin", replace: true });
          return;
        }
        if (!data.company) {
          // replace, so the back button can't bounce the user between
          // dashboard and onboarding.
          navigate({ to: "/onboarding", replace: true });
          return;
        }
        setWorkspace(data);
        try {
          const s = await fetchStats({ data: { days } });
          if (!cancelled) setStatsData(s);
        } catch {}
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load workspace status", error);
        if (cancelled) return;
        if (String(error).includes("Unauthorized")) {
          navigate({ to: "/auth", replace: true });
          return;
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchWorkspace, fetchStats, navigate, viewAsUser, days]);

  if (loading)
    return (
      <DashboardShell title=" " subtitle="">
        <DashboardSkeleton />
      </DashboardShell>
    );

  const company = workspace?.company ?? { name: "Workspace" };
  const totals = statsData?.totals ?? { conversations: 0, messages: 0, leads: 0 };
  const series = statsData?.series ?? [];
  const activity: Array<{ kind: string; at: string; who: string; msg: string; tag: string }> =
    statsData?.activity ?? [];
  const kpis = [
    {
      label: t("app.dashboard.stats.conversations"),
      value: totals.conversations,
      key: "conversations",
      icon: MessageSquare,
      c: "168",
    },
    {
      label: t("app.dashboard.stats.leads"),
      value: totals.leads,
      key: "leads",
      icon: Users,
      c: "240",
    },
    {
      label: t("app.dashboard.statMessages", { defaultValue: "Messages" }),
      value: totals.messages,
      key: "messages",
      icon: Activity,
      c: "290",
    },
  ];
  const usage = workspace?.usage ?? {
    plan: company.plan ?? "free",
    used: company.monthly_conversations ?? 0,
    limit: planLimit(company.plan ?? "free"),
  };
  const actions = buildActions(statsData?.attention ?? null, usage, t);
  const publicUrl = `https://realitytwin.io/marketplace/${company.id}`;
  const data = series.slice(-14).map((r: any, i: number) => ({ d: i, v: Number(r.messages) }));
  const bars = series.slice(-7).map((r: any) => ({
    d: new Date(r.day).toLocaleDateString(undefined, { weekday: "short" }),
    leads: Number(r.leads),
  }));
  const subtitle =
    totals.conversations > 0
      ? t(
          totals.conversations === 1
            ? "app.dashboard.subtitleConv_one"
            : "app.dashboard.subtitleConv_other",
          {
            n: totals.conversations,
            defaultValue:
              totals.conversations === 1
                ? "Your twin handled {{n}} conversation in the last 30 days."
                : "Your twin handled {{n}} conversations in the last 30 days.",
          },
        )
      : t("app.dashboard.subtitleEmpty", {
          defaultValue:
            "Your twin is ready. Add knowledge and share your chat widget to start collecting real conversations.",
        });

  return (
    <DashboardShell
      title={t("app.dashboard.welcomeBack", { name: company.name })}
      subtitle={subtitle}
      actions={
        <>
          <div className="hidden sm:flex gap-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`text-xs px-3 py-1.5 rounded-full ${days === d ? "brand-gradient text-black" : "glass border border-border text-muted-foreground hover:bg-white/5"}`}
              >
                {d}d
              </button>
            ))}
          </div>
          {company.is_public && (
            <button
              onClick={() => {
                navigator.clipboard
                  .writeText(publicUrl)
                  .then(() =>
                    toast.success(
                      t("app.dashboard.linkCopied", { defaultValue: "Public twin link copied" }),
                    ),
                  )
                  .catch(() =>
                    toast.error(
                      t("app.dashboard.linkCopyFail", { defaultValue: "Could not copy" }),
                    ),
                  );
              }}
              className="rounded-full glass border border-border px-3 py-2 text-sm hover:bg-white/5 flex items-center gap-1.5"
            >
              <Link2 className="h-3.5 w-3.5" />{" "}
              {t("app.dashboard.shareTwin", { defaultValue: "Share twin" })}
            </button>
          )}
          <Link
            to="/twin"
            className="rounded-full brand-gradient text-black px-4 py-2 text-sm font-medium"
          >
            {t("app.dashboard.openTwin")}
          </Link>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="glass-strong rounded-2xl p-5 relative overflow-hidden"
          >
            <Sparkline series={series} dataKey={s.key} hue={s.c} />
            <div className="relative flex items-start justify-between">
              <s.icon className="h-5 w-5" style={{ color: `oklch(0.82 0.18 ${s.c})` }} />
              <DeltaBadge delta={weekDelta(series, s.key)} />
            </div>
            <div className="relative mt-4 font-display text-3xl font-semibold">
              {Number(s.value).toLocaleString()}
            </div>
            <div className="relative text-xs text-muted-foreground mt-1 mb-8">{s.label}</div>
          </motion.div>
        ))}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="glass-strong rounded-2xl p-5 flex items-center gap-4"
        >
          <ScoreRing score={Number(company.ai_score ?? 0)} />
          <div>
            <div className="text-xs text-muted-foreground">{t("app.dashboard.stats.aiPerf")}</div>
            <div className="font-display text-lg font-semibold capitalize">
              {company.twin_status}
            </div>
            <Link to="/knowledge" className="text-xs text-[oklch(0.82_0.18_168)] hover:underline">
              {t("app.dashboard.improveScore", { defaultValue: "Improve score" })} →
            </Link>
          </div>
        </motion.div>
      </div>

      {actions.length > 0 ? (
        <div className="glass-strong rounded-2xl p-5 mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg font-semibold flex items-center gap-2">
              <Flame className="h-4 w-4 text-[oklch(0.72_0.19_25)]" />{" "}
              {t("app.dashboard.actionCenter", { defaultValue: "Action Center" })}
            </div>
            <span className="text-xs text-muted-foreground">
              {t("app.dashboard.actionCenterHint", { defaultValue: "What needs you right now" })}
            </span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {actions.map((a) => (
              <Link
                key={a.text}
                to={a.to}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition hover:bg-white/5 ${
                  a.urgent
                    ? "border-[oklch(0.72_0.19_25_/_0.4)] bg-[oklch(0.72_0.19_25_/_0.06)]"
                    : "border-border glass"
                }`}
              >
                <span
                  className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{
                    background: `oklch(0.82 0.15 ${a.hue} / 0.15)`,
                    color: `oklch(0.82 0.15 ${a.hue})`,
                  }}
                >
                  <a.icon className="h-4 w-4" />
                </span>
                <span className="text-sm flex-1 min-w-0">
                  {a.count != null && <span className="font-semibold">{a.count} </span>}
                  {a.text}
                </span>
                <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      ) : (
        statsData?.attention && (
          <div className="glass-strong rounded-2xl p-4 mt-4 flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-[oklch(0.82_0.18_168)]" />
            <span className="text-sm text-muted-foreground">
              {t("app.dashboard.allClear", {
                defaultValue:
                  "All clear — your twin is live, public and answering. New leads and matches land here.",
              })}
            </span>
          </div>
        )
      )}

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="glass-strong rounded-2xl p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                {t("app.dashboard.convLabel")}
              </div>
              <div className="font-display text-xl font-semibold">{t("app.dashboard.last14")}</div>
            </div>
            <div className="text-xs text-[oklch(0.82_0.18_168)]">{t("app.dashboard.vsPrev")}</div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="oklch(0.82 0.18 168)" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="oklch(0.82 0.18 168)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="d" hide />
                <YAxis hide />
                <Tooltip
                  contentStyle={{
                    background: "#0F1117",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="v"
                  stroke="oklch(0.82 0.18 168)"
                  strokeWidth={2}
                  fill="url(#g)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass-strong rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
            {t("app.dashboard.leadPipeline")}
          </div>
          <div className="font-display text-xl font-semibold mb-3">
            {t("app.dashboard.thisWeek")}
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bars}>
                <XAxis
                  dataKey="d"
                  stroke="oklch(0.65 0.02 260)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis hide />
                <Tooltip
                  contentStyle={{
                    background: "#0F1117",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  cursor={{ fill: "rgba(255,255,255,0.05)" }}
                />
                <Bar dataKey="leads" fill="oklch(0.70 0.18 240)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="glass-strong rounded-2xl p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-[oklch(0.82_0.18_168)]" />{" "}
              {t("app.dashboard.liveActivity")}
            </div>
            <span className="text-xs text-muted-foreground">{t("app.dashboard.realTime")}</span>
          </div>
          <ul className="divide-y divide-border">
            {activity.length === 0 ? (
              <li className="py-8 text-center text-sm text-muted-foreground">
                {t("app.dashboard.noActivity", {
                  defaultValue:
                    "No activity yet. Once visitors chat with your Twin, live events will appear here.",
                })}
              </li>
            ) : (
              activity.map((e, i) => {
                const color = e.kind === "lead" ? "240" : "168";
                const ago = timeAgo(e.at);
                return (
                  <li key={i} className="py-3 flex items-center gap-3">
                    <div
                      className="h-2 w-2 rounded-full"
                      style={{ background: `oklch(0.82 0.18 ${color})` }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">
                        <span className="font-medium">{e.who}</span>{" "}
                        <span className="text-muted-foreground">{e.msg}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">{ago}</div>
                    </div>
                    <span
                      className="text-xs glass px-2 py-0.5 rounded-md"
                      style={{ color: `oklch(0.82 0.18 ${color})` }}
                    >
                      {e.tag}
                    </span>
                  </li>
                );
              })
            )}
          </ul>
        </div>

        <div className="glass-strong rounded-2xl p-5">
          <div className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
            <Network className="h-4 w-4 text-[oklch(0.68_0.19_290)]" />{" "}
            {t("app.dashboard.twinStatus")}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative h-16 w-16 overflow-hidden rounded-full brand-gradient glow-mint flex items-center justify-center text-black font-display font-bold text-xl">
              {company.logo_url ? (
                <img
                  src={company.logo_url}
                  alt={`${company.name} logo`}
                  className="h-full w-full object-cover"
                />
              ) : (
                (company.name?.[0] ?? "?")
              )}
            </div>
            <div>
              <div className="font-semibold">{company.name}</div>
              <div className="text-xs text-[oklch(0.82_0.18_168)] flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[oklch(0.82_0.18_168)] animate-pulse" />{" "}
                {t("app.dashboard.live")}
              </div>
            </div>
          </div>
          <dl className="mt-5 space-y-2 text-sm">
            {[
              [t("app.dashboard.pagesIndexed"), company.pages_indexed],
              [t("app.dashboard.documents"), company.documents_indexed],
              [
                t("app.dashboard.knowledgeSize"),
                t("app.dashboard.chunks", { n: company.knowledge_size }),
              ],
              [t("app.dashboard.aiScore"), `${company.ai_score}%`],
            ].map(([k, v]) => (
              <div key={String(k)} className="flex justify-between">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="font-medium">{v}</dd>
              </div>
            ))}
          </dl>
          <PlanUsage plan={usage.plan} used={usage.used} limit={usage.limit} />
          <Link
            to="/twin"
            className="mt-5 inline-flex items-center gap-1.5 text-sm text-[oklch(0.82_0.18_168)] hover:underline"
          >
            {t("app.dashboard.openAITwin")} <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </DashboardShell>
  );
}
